import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, ChevronDown, RefreshCw, Download } from 'lucide-react';
import Breadcrumb, { BREADCRUMB_ACTION_BTN } from '../../../components/Breadcrumb/Breadcrumb';
import Table, { TABLE_THEME } from '../../../components/Table/Table';
import payRollService from '../../../services/PayrollService';
import {
  fetchSalaryComponentSummary,
} from '../../../store/slices/payrollSlice';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const FIELD = `${TABLE_THEME.fieldBase} ${TABLE_THEME.fieldFocus}`;
const LABEL = TABLE_THEME.fieldLabel;

// ─── Selectors ──────────────────────────────────────────────────────────────
const selectSalaryComponentSummary = (state) => state.payroll?.salaryComponentSummary?.employees || [];
const selectSalarySummaryLoading = (state) => state.payroll?.salaryComponentSummary?.loading || false;
const selectSalarySummaryError = (state) => state.payroll?.salaryComponentSummary?.error || null;

/*
|--------------------------------------------------------------------------
| Helper: fetch a real payroll period ID to use when calling
| GetSalaryComponentSummsery (passing 0 / no ID returns rows with blank
| HPP_PERIOD_NAME across the board, which breaks the dropdown).
|--------------------------------------------------------------------------
*/
async function resolveValidPeriodId() {
  try {
    const response = await payRollService.GetPayRollPeriodList();
    const resultSet = response?.data?.ResultSet || response?.ResultSet || [];
    const firstPeriodId = resultSet[0]?.HPP_PAYROLL_PERIOD_ID;
    return firstPeriodId ;
  } catch (err) {
    console.error('Failed to fetch a valid period ID, falling back:', err);
  }
}

/*
|--------------------------------------------------------------------------
| Main Component
|--------------------------------------------------------------------------
*/
function SalarySummary() {
  const dispatch = useDispatch();

  const employees = useSelector(selectSalaryComponentSummary);
  const loading = useSelector(selectSalarySummaryLoading);
  const error = useSelector(selectSalarySummaryError);

  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPeriodName, setSelectedPeriodName] = useState('');
  const ROWS_PER_PAGE = 10;

  // Fetch the full dataset once on mount, using a real period ID so the
  // backend's join actually populates HPP_PERIOD_NAME on the rows.
  useEffect(() => {
    let cancelled = false;

    const loadInitialData = async () => {
      const periodId = await resolveValidPeriodId();
      if (!cancelled) {
        dispatch(fetchSalaryComponentSummary(periodId));
      }
    };

    loadInitialData();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const handleRefresh = () => {
    resolveValidPeriodId().then((periodId) => {
      dispatch(fetchSalaryComponentSummary(periodId));
    });
  };

  // Build the dropdown from distinct, non-blank HPP_PERIOD_NAME values in the data
const availablePeriodNames = useMemo(() => {
  if (!employees || !Array.isArray(employees)) return [];

  const seen = new Set();
  const names = [];

  employees.forEach((emp) => {
    const name = (emp.HPP_PERIOD_NAME || '').trim();

    if (name && !seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  });

  // Sort newest period first
  names.sort((a, b) => {
    const dateA = new Date(`${a} 1`);
    const dateB = new Date(`${b} 1`);

    return dateB - dateA;
  });

  return names;
}, [employees]);

  // Default to the first available period once data loads
  useEffect(() => {
    if (!selectedPeriodName && availablePeriodNames.length > 0) {
      setSelectedPeriodName(availablePeriodNames[0]);
    }
  }, [availablePeriodNames, selectedPeriodName]);

  // Rows belonging to the selected period name, deduped per employee as a safety net
  const periodEmployees = useMemo(() => {
    if (!employees || !Array.isArray(employees) || !selectedPeriodName) return [];

    const rows = employees.filter(
      (emp) => (emp.HPP_PERIOD_NAME || '').trim() === selectedPeriodName
    );

    const seenEmp = new Set();
    return rows.filter((row) => {
      const key = row.HED_EMPLOYEE_ID || row.HED_SERVICE_NO;
      if (seenEmp.has(key)) return false;
      seenEmp.add(key);
      return true;
    });
  }, [employees, selectedPeriodName]);

  const handlePeriodChange = (newPeriodName) => {
    setSelectedPeriodName(newPeriodName);
    setCurrentPage(1);
    setSearch('');
  };

  const filteredEmployees = useMemo(() => {
    if (!periodEmployees || !Array.isArray(periodEmployees)) return [];

    const q = search.trim().toLowerCase();
    if (!q) return periodEmployees;

    return periodEmployees.filter((employee) => {
      const serviceNo = String(employee.HED_SERVICE_NO || '').toLowerCase();
      const reportName = String(employee.HED_REPORT_NAME || '').toLowerCase();
      const location = String(employee.HLD_LOCATION_NAME || '').toLowerCase();
      const nic = String(employee.HED_NIC_NO || '').toLowerCase();
      const designation = String(employee.HED_DESIGNATION_NAME || '').toLowerCase();
      const category = String(employee.HEC_CATEGORY_NAME || '').toLowerCase();
      const department = String(employee.HDD_DEPARTMENT_NAME || '').toLowerCase();

      return serviceNo.includes(q) ||
        reportName.includes(q) ||
        location.includes(q) ||
        nic.includes(q) ||
        designation.includes(q) ||
        category.includes(q) ||
        department.includes(q);
    });
  }, [periodEmployees, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredEmployees]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';

      const day = String(date.getDate()).padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (e) {
      return '-';
    }
  };

  const formatStatus = (status) => {
    const statusMap = {
      'Current': 'Current',
      'ExEmployee': 'ExEmployee',
      'Probation': 'Probation',
      'True': 'Current',
      'False': 'ExEmployee',
    };

    return statusMap[status] || status || '-';
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'Current': 'bg-green-100 text-green-800',
      'ExEmployee': 'bg-red-100 text-red-800',
      'Probation': 'bg-yellow-100 text-yellow-800',
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  const exportToExcel = () => {
    if (!filteredEmployees || filteredEmployees.length === 0) {
      alert('No data to export');
      return;
    }

    // Get current date and time
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    const timeStr = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    // Get month from selectedPeriodName or current date
    let monthDisplay = selectedPeriodName || '';
    if (!monthDisplay) {
      const currentMonth = now.toLocaleString('en-US', { month: 'short' });
      const currentYear = now.getFullYear();
      monthDisplay = `${currentMonth}-${currentYear}`;
    }

    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Salary Summary');

    // Define headers
    const headers = [
      'Staff Id', 'Name', 'Location', 'NIC', 'Date of Joining',
      'Date of Leaving', 'Date of Birth', 'Gender', 'Designation',
      'Employment Category', 'Status', 'Group', 'Department',
      'Cost Center', 'Total Earnings to Gross'
    ];

    // --- ADD HEADING (Row 1)
    const headingText = `Salary Component Report Month ${monthDisplay} | Time ${dateStr} ${timeStr}`;
    const headingRow = worksheet.addRow([headingText]);
    worksheet.mergeCells(`A1:${String.fromCharCode(64 + headers.length)}1`);

    headingRow.getCell(1).alignment = {
      horizontal: 'center',
      vertical: 'center'
    };
    headingRow.getCell(1).font = {
      bold: true,
      size: 14
    };
    headingRow.height = 30;

    // --- ADD EMPTY ROW (Row 2)
    worksheet.addRow([]);

    // --- ADD HEADERS (Row 3)
    const headerRow = worksheet.addRow(headers);
    headerRow.height = 25;

    headerRow.eachCell((cell, colNumber) => {
      cell.alignment = {
        horizontal: 'center',
        vertical: 'center'
      };
      cell.font = {
        bold: true,
        size: 11
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // --- ADD DATA ROWS ---
    filteredEmployees.forEach((emp) => {
      const dataRow = worksheet.addRow([
        emp.HED_SERVICE_NO || '',
        emp.HED_REPORT_NAME || '',
        emp.HLD_LOCATION_NAME || '',
        emp.HED_NIC_NO || '',
        formatDate(emp.HED_DATE_OF_JOINING),
        formatDate(emp.HED_LEAVING_DATE),
        formatDate(emp.HED_DATE_OF_BIRTH),
        emp.HED_GENDER === 'M' ? 'Male' : emp.HED_GENDER === 'F' ? 'Female' : emp.HED_GENDER || '-',
        emp.HED_DESIGNATION_NAME || '',
        emp.HEC_CATEGORY_NAME || '',
        formatStatus(emp.HED_IS_ACTIVE),
        emp.HED_GROUP || '',
        emp.HDD_DEPARTMENT_NAME || '',
        emp.HED_COSTCENTER || '',
        Number(emp.HPH_NET_SALARY || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      ]);

      dataRow.eachCell((cell) => {
        cell.alignment = {
          horizontal: 'center',
          vertical: 'center'
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });

    // --- SET COLUMN WIDTHS ---
    worksheet.columns = [
      { width: 12 }, // Staff Id
      { width: 25 }, // Name
      { width: 15 }, // Location
      { width: 15 }, // NIC
      { width: 15 }, // Date of Joining
      { width: 15 }, // Date of Leaving
      { width: 15 }, // Date of Birth
      { width: 10 }, // Gender
      { width: 20 }, // Designation
      { width: 20 }, // Employment Category
      { width: 12 }, // Status
      { width: 15 }, // Group
      { width: 20 }, // Department
      { width: 15 }, // Cost Center
      { width: 20 }  // Total Earnings to Gross
    ];

    const lastColIndex = headers.length - 1;
    worksheet.columns[lastColIndex].width = 21;

    // Also make sure the worksheet shows gridlines
    worksheet.properties.outlineProperties = {
      summaryBelow: true,
      summaryRight: true
    };

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const fileName = `Salary_Component_${selectedPeriodName || 'Period'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);
    }).catch((error) => {
      console.error('Error generating Excel file:', error);
      alert('Error generating Excel file. Please try again.');
    });
  };

  // ─── Define employeeColumns inside the component ──────────────────────
  const employeeColumns = [
    {
      key: 'HED_SERVICE_NO',
      label: 'Staff Id',
      className: 'font-medium text-gray-900'
    },
    {
      key: 'HED_REPORT_NAME',
      label: 'Name',
      className: 'text-gray-700 font-medium'
    },
    {
      key: 'HLD_LOCATION_NAME',
      label: 'Location',
      className: 'text-gray-600'
    },
    {
      key: 'HED_NIC_NO',
      label: 'NIC',
      className: 'text-gray-600 tabular-nums'
    },
    {
      key: 'HED_DATE_OF_JOINING',
      label: 'Date of Joining',
      className: 'text-gray-600',
      render: (row) => formatDate(row.HED_DATE_OF_JOINING)
    },
    {
      key: 'HED_LEAVING_DATE',
      label: 'Date of Leaving',
      className: 'text-gray-600',
      render: (row) => formatDate(row.HED_LEAVING_DATE)
    },
    {
      key: 'HED_DATE_OF_BIRTH',
      label: 'Date of Birth',
      className: 'text-gray-600',
      render: (row) => formatDate(row.HED_DATE_OF_BIRTH)
    },
    {
      key: 'HED_GENDER',
      label: 'Gender',
      className: 'text-gray-600',
      render: (row) => row.HED_GENDER === 'M' ? 'Male' : row.HED_GENDER === 'F' ? 'Female' : row.HED_GENDER || '-'
    },
    {
      key: 'HED_DESIGNATION_NAME',
      label: 'Designation',
      className: 'text-gray-700'
    },
    {
      key: 'HEC_CATEGORY_NAME',
      label: 'Employment Category',
      className: 'text-gray-600'
    },
    {
      key: 'HED_IS_ACTIVE',
      label: 'Status',
      className: 'text-gray-600',
      render: (row) => {
        const status = formatStatus(row.HED_IS_ACTIVE);
        const colorClass = getStatusColor(status);
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
            {status}
          </span>
        );
      }
    },
    {
      key: 'HED_GROUP',
      label: 'Group',
      className: 'text-gray-600'
    },
    {
      key: 'HDD_DEPARTMENT_NAME',
      label: 'Department',
      className: 'text-gray-600'
    },
    {
      key: 'HED_COSTCENTER',
      label: 'Cost Center',
      className: 'text-gray-600'
    },
    {
      key: 'HPH_NET_SALARY',
      label: 'Total Earnings to Gross',
      className: 'font-semibold text-green-600 text-right',
      align: 'right',
      render: (row) => {
        const salary = Number(row.HPH_NET_SALARY || 0);
        if (salary === 0) return '-';
        return `${salary.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      }
    },
  ];

  // ─── Show error state ──────────────────────────────────────────────────
  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          <h3 className="font-semibold">Error loading data</h3>
          <p>{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-2 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* ── Header ─────────────────────────────────────────────── */}
      <Breadcrumb
        trail={['Payroll', 'Salary Details Report']}
        title="Salary Details Report"
        subtitle={`View employee Salary Details Report for ${selectedPeriodName || 'selected period'}.`}
        action={
          <div className="flex gap-2">
            <button
              onClick={exportToExcel}
              className={`${BREADCRUMB_ACTION_BTN} bg-green-600 hover:bg-green-700 text-white`}
              disabled={!filteredEmployees || filteredEmployees.length === 0}
            >
              <Download size={15} />
              Export
            </button>
            <button
              onClick={handleRefresh}
              className={BREADCRUMB_ACTION_BTN}
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        }
      />

      {/* ── Filters ────────────────────────────────────────────── */}
      <div>
        <span className={LABEL}>Payroll Period</span>
        <select
          value={selectedPeriodName}
          onChange={(e) => handlePeriodChange(e.target.value)}
          className={`${FIELD} w-64`}
        >
          {availablePeriodNames.length === 0 && (
            <option value="">No periods available</option>
          )}
          {availablePeriodNames.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </div>

      {/* ── Table ────────────────────────────────────────────────────── */}
      <Table
        title="Employee Details"
        meta={`${filteredEmployees.length} of ${employees?.length || 0} employee${employees?.length === 1 ? '' : 's'}${selectedPeriodName ? ` for ${selectedPeriodName}` : ''}`}
        columns={employeeColumns}
        rows={filteredEmployees}
        rowKey={(row) => row.HED_EMPLOYEE_ID || row.HED_SERVICE_NO || Math.random().toString()}
        loading={loading}
        loadingMessage="Loading employee details..."
        emptyMessage={
          !employees || employees.length === 0
            ? 'No employee data available. Select a payroll period to view data.'
            : 'No results match your search criteria.'
        }
        minWidth={1400}
        pageSize={ROWS_PER_PAGE}
        scrollClassName=""
        emptyValue="-"
        defaultSort={{ key: 'HED_SERVICE_NO', dir: 'asc' }}
      />
    </div>
  );
}

export default SalarySummary;